/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { PlanId } from "components/common/boundary/PlanId";
import { filterPlanResultsToPeriod } from "components/common/plan/utils";
import React, { useEffect, useRef, useState } from "react";
import { generatedApi } from "src/store/api/generatedApi";
import { useAppDispatch, useAppSelector } from "src/store/store";
import { useTenant } from "./settings";

interface UseDeployPrefetchResult {
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  error?: unknown;
}

export const useDeployPrefetch = (
  planId: PlanId,
  enabled = true
): UseDeployPrefetchResult => {
  const tenantName = useTenant();
  const dispatch = useAppDispatch();
  const prefetchedRef = useRef(false);

  const [isLoading, setIsLoading] = useState(enabled);
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState<unknown>();

  const { data: plan } = generatedApi.useGetPlanQuery(
    { tenantName, planId },
    { skip: !enabled }
  );

  const { data: backups } = generatedApi.useGetBackupsQuery(
    { tenantName, planId },
    { skip: !enabled }
  );

  const [getPlanResultsBatch] = generatedApi.useGetPlanResultsBatchMutation();

  const allPlanMixesLoaded = useAppSelector((state) => {
    if (!enabled) return true;
    if (!plan?.summary?.period_summary || !backups?.backups) {
      return false;
    }

    for (const period of plan.summary.period_summary) {
      if (period.is_deployable) {
        const data = generatedApi.endpoints.getPlanResults.select({
          tenantName,
          planId,
          period: period.period,
        })(state).data;
        if (!data) return false;
      }
    }

    for (const backup of backups.backups) {
      if (backup.plan_id !== null) {
        const data = generatedApi.endpoints.getPlanResults.select({
          tenantName,
          planId: backup.plan_id,
          period: 1,
        })(state).data;
        if (!data) return false;
      }
    }

    return true;
  });

  useEffect(() => {
    if (!enabled) {
      // eslint-disable-next-line @eslint-react/set-state-in-effect -- early return when prefetch disabled
      setIsLoading(false);
      return;
    }

    if (allPlanMixesLoaded) {
      // eslint-disable-next-line @eslint-react/set-state-in-effect -- all data loaded, stop loading state
      setIsLoading(false);
      prefetchedRef.current = true;
      return;
    }

    if (prefetchedRef.current || !plan || !backups) {
      return;
    }

    const requests: { plan_id: number; period: number }[] = [];

    if (plan?.summary?.period_summary) {
      plan.summary.period_summary
        .filter((period) => period.is_deployable)
        .forEach((period) => {
          requests.push({ plan_id: planId, period: period.period });
        });
    }

    if (backups?.backups) {
      backups.backups.forEach((backup) => {
        if (backup.plan_id !== null) {
          requests.push({ plan_id: backup.plan_id, period: 1 });
        }
      });
    }

    if (requests.length > 0) {
      prefetchedRef.current = true;

      void getPlanResultsBatch({ tenantName, requests })
        .unwrap()
        .then((response) => {
          response.results.forEach((item) => {
            if (item.response) {
              void dispatch(
                generatedApi.util.upsertQueryData(
                  "getPlanResults",
                  {
                    tenantName,
                    planId: item.plan_id,
                    period: item.period,
                  },
                  filterPlanResultsToPeriod(item.response, item.period)
                )
              );
            }
          });
        })
        .catch((err: unknown) => {
          console.error("Failed to prefetch plan mixes:", err);
          setIsError(true);
          setError(err);
          setIsLoading(false);
          prefetchedRef.current = false;
        });
    }
  }, [
    enabled,
    tenantName,
    planId,
    plan,
    backups,
    getPlanResultsBatch,
    dispatch,
    allPlanMixesLoaded,
  ]);

  React.useEffect(() => {
    if (!enabled || !plan?.summary?.period_summary || !backups?.backups) return;

    const subscriptions: { unsubscribe: () => void }[] = [];

    const periods = plan.summary.period_summary;
    const backupPlanIds = backups.backups
      .filter((b) => b.plan_id !== null)
      .map((b) => b.plan_id as PlanId);
    const allPlans = [planId, ...backupPlanIds];

    allPlans.forEach((pid) => {
      periods.forEach((period) => {
        const subscription = dispatch(
          generatedApi.endpoints.getPlanResults.initiate(
            {
              tenantName,
              planId: pid,
              period: period.period,
            },
            { subscribe: true, forceRefetch: false }
          )
        );
        subscriptions.push(subscription);
      });
    });

    return () => {
      subscriptions.forEach((sub) => {
        sub.unsubscribe();
      });
    };
  }, [enabled, plan, backups, planId, tenantName, dispatch]);

  return {
    isLoading,
    isSuccess: !isLoading && !isError && allPlanMixesLoaded,
    isError,
    error,
  };
};

export const shouldFetchDeploy = (): boolean => {
  return true;
};
